/**
 * Fase 0C de seguridad — callables de identidad (functions/identity).
 *
 * Las altas y los cambios de residentes, unidades, edificios y staff pasan por
 * el servidor: el cliente ya no escribe users / apartments / userbuilding /
 * occupancies / admin directo (las reglas 0D lo van a impedir). Cada función
 * devuelve `res.data` del callable; los errores llegan como FirebaseError con
 * `code` (functions/permission-denied, functions/failed-precondition, …).
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// Los lotes (importación de unidades, baja de edificio) pueden tardar minutos:
// el servidor corta a los 540 s y el cliente espera lo mismo.
const LARGOS = new Set(["importarUnidades", "approveBuildingDeletion"]);

const llamar = async (nombre, data = {}) => {
  const fn = httpsCallable(functions, nombre, { timeout: LARGOS.has(nombre) ? 540000 : 180000 });
  const res = await fn(data);
  return res.data;
};

/** Id para reintentos idempotentes (el servidor no repite la operación). */
export const nuevoRequestId = () => {
  const base =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return base.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
};

const textoDelError = (e) => (e && e.message ? String(e.message) : "").replace(/^(FirebaseError:\s*)/, "").trim();

/**
 * true si el error no trae mensaje del servidor: red caída, timeout o
 * excepción no controlada, donde el SDK pone el código como texto
 * ("internal", "INTERNAL", "deadline-exceeded", "DEADLINE_EXCEEDED").
 */
export const sinMensajeDelServidor = (e) => {
  const msg = textoDelError(e);
  return !msg || /^[a-z]+([-_][a-z]+)*$/i.test(msg);
};

/** Mensaje legible de un error de callable; sin mensaje del servidor, el fallback. */
export const mensajeDeError = (e, fallback = "No se pudo completar la acción.") =>
  sinMensajeDelServidor(e) ? fallback : textoDelError(e);

/** Código corto del error ("permission-denied", "failed-precondition", …). */
export const codigoDeError = (e) => String((e && e.code) || "").replace(/^functions\//, "");

// ── Cuenta propia ──
export const vincularMiCuenta = () => llamar("vincularMiCuenta", {});
export const updateMyContact = (data) => llamar("updateMyContact", data);
// Recalcula los permisos propios (user_access) sin esperar a los triggers.
export const refreshMyAccess = () => llamar("refreshMyAccess", {});

// ── Residentes y unidades ──
export const inviteResident = (data) => llamar("inviteResident", data);
export const importarUnidades = (data) => llamar("importarUnidades", data);
export const residentAccountAction = (data) => llamar("residentAccountAction", data);
export const updateResidentContact = (data) => llamar("updateResidentContact", data);
export const assignOccupants = (data) => llamar("assignOccupants", data);
export const occupancyAction = (data) => llamar("occupancyAction", data);
export const deleteUnit = (data) => llamar("deleteUnit", data);

// ── Edificios ──
export const createBuilding = (data) => llamar("createBuilding", data);
export const setBuildingAdmin = (data) => llamar("setBuildingAdmin", data);
export const approveBuildingDeletion = (data) => llamar("approveBuildingDeletion", data);

// ── Staff ──
export const createStaffUser = (data) => llamar("createStaffUser", data);
export const updateStaffUser = (data) => llamar("updateStaffUser", data);
export const removeStaffUser = (data) => llamar("removeStaffUser", data);
